import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { Box, Text, TextInput, PasswordInput, Button, Group, Stack, Avatar, Badge, SimpleGrid, Divider, Select } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { getMyProfile, updateMyProfile, getMyLineLinkStatus, unlinkMyLine } from '../api/client'
import { IconBrandLine, IconCircleCheck, IconRefresh } from '@tabler/icons-react'
import { useAuth } from '../hooks/useAuth'
import i18n from '../i18n'
import SignaturePad from '../components/SignaturePad'

export default function ProfilePage() {
  const { t } = useTranslation()
  const { roles } = useAuth()
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const [profile, setProfile] = useState<any>(null)
  const [form, setForm] = useState({ fullName: '', email: '', position: '', phone: '', newPassword: '', preferredLanguage: localStorage.getItem('lang') ?? 'th', signatureImage: '' })
  const [saving, setSaving] = useState(false)
  const [savingSignature, setSavingSignature] = useState(false)
  const [signatureDirty, setSignatureDirty] = useState(false)
  const [lineStatus, setLineStatus] = useState<{ linked: boolean; code?: string; linkedAt?: string } | null>(null)
  const [lineLoading, setLineLoading] = useState(false)
  const [unlinking, setUnlinking] = useState(false)

  const loadLineStatus = () => {
    setLineLoading(true)
    getMyLineLinkStatus().then(r => setLineStatus(r.data)).finally(() => setLineLoading(false))
  }

  const handleUnlinkLine = async () => {
    setUnlinking(true)
    try {
      await unlinkMyLine()
      notifications.show({ color: 'teal', message: t('profile.lineUnlinked', 'Unlinked. A new code has been generated if you want to link again.') })
      loadLineStatus()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setUnlinking(false) }
  }

  useEffect(() => {
    getMyProfile().then((r) => {
      setProfile(r.data)
      setForm({
        fullName: r.data.fullName ?? '',
        email: r.data.email ?? '',
        position: r.data.position ?? '',
        phone: r.data.phone ?? '',
        newPassword: '',
        preferredLanguage: r.data.preferredLanguage ?? localStorage.getItem('lang') ?? 'th',
        signatureImage: r.data.signatureImage ?? '',
      })
    })
    loadLineStatus()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload: any = { ...form }
      if (!payload.newPassword) delete payload.newPassword
      else { payload.password = payload.newPassword; delete payload.newPassword }
      await updateMyProfile(payload)
      // apply language immediately
      localStorage.setItem('lang', form.preferredLanguage)
      i18n.changeLanguage(form.preferredLanguage)
      notifications.show({ color: 'lime', message: t('profile.saveSuccess') })
      setSignatureDirty(false)
      setProfile((p: any) => ({ ...p, signatureImage: form.signatureImage }))
    } catch {
      notifications.show({ color: 'red', message: 'Error saving profile' })
    } finally { setSaving(false) }
  }

  const handleSaveSignature = async () => {
    setSavingSignature(true)
    try {
      await updateMyProfile({ signatureImage: form.signatureImage })
      notifications.show({ color: 'lime', message: t('profile.signatureSaveSuccess', 'Signature saved') })
      setSignatureDirty(false)
      setProfile((p: any) => ({ ...p, signatureImage: form.signatureImage }))
    } catch {
      notifications.show({ color: 'red', message: 'Error saving signature' })
    } finally { setSavingSignature(false) }
  }

  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))

  return (
    <Box p={isSmall ? 16 : 32} maw={isMobile ? '100%' : 640}>
      <Box mb={isMobile ? 'md' : 'xl'}>
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
          {t('profile.title')}
        </Text>
        <Text size="sm" c="dimmed">{t('profile.subtitle')}</Text>
      </Box>

      {/* Avatar + roles */}
      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', padding: isMobile ? 16 : 24 }} mb="lg">
        <Group>
          <Avatar size={isMobile ? 48 : 64} radius="xl" color="teal" variant="filled" style={{ fontSize: isMobile ? 22 : 28 }}>
            {profile?.username?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Text fw={700} size={isMobile ? 'md' : 'lg'} style={{ fontFamily: 'Inter, sans-serif' }}>
              {profile?.fullName || profile?.username}
            </Text>
            <Text size="sm" c="dimmed">{profile?.position ?? '-'}</Text>
            <Group gap={4} mt={4}>
              {roles.map((r) => <Badge key={r} size="xs" color="blue" variant="light">{r}</Badge>)}
            </Group>
          </Box>
        </Group>
      </Box>

      {/* Edit form */}
      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', padding: isMobile ? 16 : 24 }}>
        <Text fw={700} mb="md">{t('profile.currentInfo')}</Text>
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label={t('users.fullName')} value={form.fullName} onChange={f('fullName')} autoComplete="off" />
            <TextInput label={t('users.email')} value={form.email} onChange={f('email')} autoComplete="off" />
            <TextInput label={t('users.position')} value={form.position} onChange={f('position')} autoComplete="off" />
            <TextInput label={t('users.phone')} value={form.phone} onChange={f('phone')} autoComplete="off" name="profile-phone-no-autofill" />
          </SimpleGrid>

          <Divider my="sm" label={t('profile.changePassword')} labelPosition="left" />

          <PasswordInput
            label={t('profile.newPassword')}
            placeholder={t("profile.passwordBlankHint","Leave blank to keep current password")}
            value={form.newPassword}
            onChange={f('newPassword')}
            autoComplete="new-password"
            style={{ maxWidth: isMobile ? '100%' : 320 }}
          />

          <Divider my="sm" label={t('profile.preferences', 'การตั้งค่า')} labelPosition="left" />

          <Divider my="sm" label={t('profile.eSignature', 'ลายเซ็นอิเล็กทรอนิกส์')} labelPosition="left" />
          <Text size="xs" c="dimmed" mb={8} style={{ maxWidth: 420 }}>
            {t('profile.eSignatureHint', 'วาดหรือแนบลายเซ็นด้านล่าง แล้วกดปุ่ม "บันทึกลายเซ็น" — ระบบจะดึงมาใช้อัตโนมัติทุกครั้งที่คุณอนุมัติรายการผ่าน LINE')}
          </Text>
          <Group align="flex-start" gap="lg" wrap="wrap">
            <Box>
              <Text size="xs" fw={500} mb={4}>{t('profile.signatureDraw', 'วาด / แนบลายเซ็นใหม่')}</Text>
              <SignaturePad
                label=""
                value={form.signatureImage}
                onSave={(data) => { setForm(p => ({ ...p, signatureImage: data })); setSignatureDirty(true) }}
                width={220}
                height={220}
                hint=""
              />
              <Group gap="xs" mt={6}>
                <Button size="xs" loading={savingSignature} disabled={!signatureDirty}
                  onClick={handleSaveSignature}>
                  {t('profile.saveSignature', 'บันทึกลายเซ็น')}
                </Button>
                {!signatureDirty && form.signatureImage && (
                  <Text size="xs" c="teal">{t('profile.signatureSaved', '✓ บันทึกแล้ว')}</Text>
                )}
                {signatureDirty && (
                  <Text size="xs" c="orange">{t('profile.signatureUnsaved', 'มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก')}</Text>
                )}
              </Group>
            </Box>

            <Box>
              <Text size="xs" fw={500} mb={4}>{t('profile.signatureCurrent', 'ลายเซ็นปัจจุบันในระบบ')}</Text>
              <Box style={{
                width: 200, height: 200, border: '1px solid #ebebeb', borderRadius: 8,
                background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {profile?.signatureImage ? (
                  <img src={profile.signatureImage} alt={t('profile.signatureCurrent', 'ลายเซ็นปัจจุบันในระบบ')}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <Text size="xs" c="dimmed" ta="center" px="sm">{t('profile.signatureNoneYet', 'ยังไม่เคยบันทึกลายเซ็นไว้')}</Text>
                )}
              </Box>
            </Box>
          </Group>

          <Select
            label={t('profile.defaultLanguage', 'ภาษาเริ่มต้น')}
            description={t('profile.defaultLanguageHint', 'ระบบจะใช้ภาษานี้ทุกครั้งที่ Login')}
            data={[
              { value: 'th', label: '🇹🇭 ภาษาไทย' },
              { value: 'en', label: '🇬🇧 English' },
            ]}
            value={form.preferredLanguage}
            onChange={(v) => setForm(p => ({ ...p, preferredLanguage: v ?? 'th' }))}
            style={{ maxWidth: 220 }}
          />

          <Divider my="sm" label={t('profile.lineConnect', 'เชื่อมต่อ LINE ส่วนตัว')} labelPosition="left" />

          {lineLoading && !lineStatus ? (
            <Text size="sm" c="dimmed">{t('common.loading', 'Loading...')}</Text>
          ) : lineStatus?.linked ? (
            <Box style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16, maxWidth: 420 }}>
              <Group gap={8} mb={4}>
                <IconCircleCheck size={18} color="#16a34a" />
                <Text fw={600} size="sm" c="#166534">{t('profile.lineLinked', 'เชื่อมต่อ LINE แล้ว')}</Text>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                {t('profile.lineLinkedHint', 'คุณจะได้รับแจ้งเตือนสำคัญ (เช่น คำขออนุมัติ) ทางแชท LINE นี้')}
              </Text>
              <Button size="xs" variant="light" color="red" loading={unlinking} onClick={handleUnlinkLine}>
                {t('profile.lineUnlink', 'ยกเลิกการเชื่อมต่อ')}
              </Button>
            </Box>
          ) : (
            <Box style={{ background: '#f8f7f4', border: '1px solid #ebebeb', borderRadius: 10, padding: 16, maxWidth: 420 }}>
              <Group gap={8} mb={8}>
                <IconBrandLine size={18} color="#06C755" />
                <Text fw={600} size="sm">{t('profile.lineNotLinked', 'ยังไม่ได้เชื่อมต่อ LINE')}</Text>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                {t('profile.lineInstructions', 'เปิดแอป LINE เพิ่มเพื่อน Official Account ของบริษัท แล้วพิมพ์ข้อความนี้ในแชท:')}
              </Text>
              <Box style={{ background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '10px 14px', fontFamily: 'monospace', fontSize: 15, fontWeight: 600, textAlign: 'center', letterSpacing: 1 }}>
                ผูกบัญชี {lineStatus?.code ?? '...'}
              </Box>
              <Button size="xs" variant="subtle" mt="sm" leftSection={<IconRefresh size={14} />} loading={lineLoading} onClick={loadLineStatus}>
                {t('profile.lineRefreshStatus', 'ผูกแล้ว? กดตรวจสอบสถานะ')}
              </Button>
            </Box>
          )}

          <Group mt="sm">
            <Button loading={saving} onClick={handleSave} fullWidth={isMobile}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Box>
  )
}
